import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale from 'data/locale';

import Store from 'store';
import ItemEditor from './ItemEditor';

import { itemGetColor, itemGetName } from 'logic/item';

import './EquipmentPane.scss';
import { D2ItemTooltip } from 'tooltips';
import { cubeOnlyItems, isSubType, itemSlotGroups, itemTypeTree, presetItems } from 'logic/itemTypes';
import { navigateToItem, NavigationBar } from './Navigation';
import ItemCreator from './ItemCreator';
import EditingContext from 'store/editingContext';
import { itemGetDescription } from 'logic/itemDescription';
import { Color, Quality } from 'common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { inventoryFindNearest, inventoryHasDuplicate } from 'logic/inventory';
import { ErrorPopup } from 'ui/utils/Overlay';
import { OverlayContext } from 'ui/utils/OverlayManager';
import { profileCanEquip, profileCanEquipType, stateIsEmpty } from 'logic/stateReducers';
import { useDragEx, useItemMover } from 'components/dragDrop';
import { EmbedItemLink, useContentCreator, useProfileId } from 'components/cclink';
import { searchRegex } from 'utils';
import { underlinerFunc } from 'ui/select';
import AdUnit from 'AdUnit';

const _L = Locale("ui-equipment");

export function itemNameColored(item, search) {
  const color = itemGetColor(item);
  if (item.quality === Quality.RUNEWORD) {
    const base = Data.items[item.base];
    if (base) {
      const prefix = (item.personalized ? `${item.personalized}'s ` : '');
      let title = Data.runes[item.unique]?.name;
      title = Locale.strings[title] || title;

      let basename = (Locale.strings[base.namestr] || base.name).split('\n');
      basename = prefix + basename[basename.length - 1];
      return <span className={`d2-color-${color}`}>{search ? underlinerFunc(title, search) : title}{' '}
        <span className={`d2-color-${Color.GRAY}`}>{search ? underlinerFunc(basename, search) : basename}</span></span>;
    }
  }
  const name = itemGetName(item, true);
  return <span className={`d2-color-${color}`}>{search ? underlinerFunc(name, search) : name}</span>;
}

const RefLink = ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>

function NewProfileNotice() {
  return (
    <div className="d2p-NewProfileNotice">
      {Data.info.pd2 ? <h3>Welcome to D2Planner (Project Diablo II Season 4)!</h3> : <h3>Welcome to D2Planner!</h3>}
      <p>Here you can create a collection of items to use in your profile.</p>
      <p>Click a paperdoll slot to assign an item, or drag items from the list.</p>
      <p>If you don't want to create a new profile from scratch, you can drop a Diablo II save file directly onto the page!</p>
      <p style={{marginTop: '1em'}}>Check out this video for an introduction to D2Planner: <RefLink href="https://www.youtube.com/watch?v=bjwH5pk77XA">https://www.youtube.com/watch?v=bjwH5pk77XA</RefLink></p>
      {!Data.info.pd2 && <p style={{marginTop: '1em'}}>Also make sure to check out our <RefLink href="https://maxroll.gg/d2/d2-drop-calculator">drop calculator</RefLink> and <RefLink href="https://maxroll.gg/d2/d2-holy-grail">holy grail</RefLink> tools.</p>}
    </div>
  );
}

const equippedLocs = {
  equipped: 'Equipped',
  merc: 'Mercenary',
  inventory: 'Inventory',
  cube: 'Cube',
  socketed: 'Socketed',
};

const ItemLine = React.memo(function ItemLine({ id, items, onSelect, equipped, itemMover, merc, link, search }) {
  const item = items[id];
  const description = itemGetDescription(item, items);

  const [, drag] = useDragEx({
    type: "ITEM",
    item: () => ({type: "ITEM", id}),
    end: (item, monitor) => itemMover({location: "library", slot: id}, monitor.getDropResult()),
  }, [id, itemMover]);

  const name = itemGetName(item);
  return (
    <li>
      <D2ItemTooltip id={id} merc={merc}>
        <span onClick={() => onSelect(id)} className={`d2p-ItemLink`} ref={drag}>{itemNameColored(item, search)}</span>
      </D2ItemTooltip>
      {!!equipped && <span className="d2p-item-equipped">{`(${_L(equippedLocs[equipped])})`}</span>}
      {!!link && <EmbedItemLink profile={link} id={id} text={name}/>}
      {!!description && <span className="d2p-item-description">{description}</span>}
    </li>
  );
});

function EquipmentList({ items, type, filter, onSelect, search: searchTerm, equipped, merc }) {
  const isEmpty = Store.useSelector(state => stateIsEmpty(state));
  const isCC = useContentCreator();
  const profileId = useProfileId();

  const search = React.useMemo(() => searchTerm ? searchRegex(searchTerm, true) : null, [searchTerm]);
  const groups = React.useMemo(() => {
    const runeword = [], unique = [], set = [], crafted = [], rare = [], magic = [], normal = [], charm = [], jewel = [];
    const presetRune = [], presetGem = [], presetOther = [], presetPotion = [], presetCube = [];
    for (const id in items) {
      const base = Data.items[items[id].base];
      if (!base || !type.filter(base.type)) continue;
      if (filter && !filter(base.type)) continue;
      if (type === itemSlotGroups.cube && id === 'box') continue;
      if (search && !itemGetName(items[id]).match(search)) continue;

      if (presetItems[id]) {
        if (cubeOnlyItems.has(id)) {
          presetCube.push(id);
        } else if (isSubType(base.type, 'rune')) {
          presetRune.push(id);
        } else if (isSubType(base.type, 'gem')) {
          presetGem.push(id);
        } else if (isSubType(base.type, 'poti')) {
          presetPotion.push(id);
        } else {
          presetOther.push(id);
        }
      } else if (isSubType(base.type, 'char')) {
        charm.push(id);
      } else if (base.type === 'jewl') {
        jewel.push(id);
      } else {
        switch (items[id].quality) {
        case Quality.RUNEWORD: runeword.push(id); break;
        case Quality.UNIQUE: unique.push(id); break;
        case Quality.SET: set.push(id); break;
        case Quality.CRAFTED: crafted.push(id); break;
        case Quality.RARE: rare.push(id); break;
        case Quality.MAGIC: magic.push(id); break;
        default: normal.push(id);
        }
      }
    }
    runeword.sort((a, b) => itemGetName(items[a]).localeCompare(itemGetName(items[b])));
    unique.sort((a, b) => itemGetName(items[a]).localeCompare(itemGetName(items[b])));
    set.sort((a, b) => itemGetName(items[a]).localeCompare(itemGetName(items[b])));
    function itemcmp(a, b) {
      const ia = items[a], ib = items[b];
      const ba = Data.items[ia.base]?.level, bb = Data.items[ib.base]?.level;
      return (ba === bb ? parseInt(a) - parseInt(b) : bb - ba);
    }
    crafted.sort(itemcmp);
    rare.sort(itemcmp);
    magic.sort(itemcmp);
    normal.sort(itemcmp);
    function jewcmp(a, b) {
      const ia = items[a], ib = items[b];
      return (ia.quality === ib.quality ? parseInt(a) - parseInt(b) : ib.quality - ia.quality);
    }
    charm.sort(jewcmp);
    jewel.sort(jewcmp);

    if (type === itemSlotGroups.inventory || type === itemSlotGroups.cube) {
      const res = [charm, presetOther, presetPotion, presetRune, presetGem, runeword, unique, set, crafted, rare, magic, normal, jewel];  
      if (type === itemSlotGroups.cube) {
        res.splice(2, 0, presetCube);
      }
      return res;
    } else {
      return [runeword, unique, set, crafted, rare, magic, normal, presetOther, presetPotion, charm, jewel];
    }
  }, [items, type, search, filter]);
  const itemMover = useItemMover();
  if (!groups.some(list => list.length)) {
    return <>
      {(type === itemTypeTree && isEmpty) ? (
        <NewProfileNotice/>
      ) : (
        <div className="d2p-NoResults">{_L`No matching items in the profile.`}</div>
      )}
      <AdUnit id="d2planner-tab-equipment" className="d2p-TabFooter d2planner-tab-ad"/>
    </>;
  }
  return <>
    <div className="d2p-ItemList">
      {groups.map((ids, idx) => {
        if (!ids.length) return null;
        return (
          <ul className="d2p-item-group" key={idx}>
            {ids.map(id => <ItemLine key={id} id={id} onSelect={onSelect} items={items} search={search}
              link={isCC && profileId} equipped={!presetItems[id] && equipped?.[id]} itemMover={itemMover} merc={merc}/>)}
          </ul>
        );
      })}
    </div>
    <AdUnit id="d2planner-tab-equipment" className="d2p-TabFooter d2planner-tab-ad"/>
  </>;
}

const SearchBox = React.memo(function SearchBox({ value, onChange }) {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const expanded = !!(hovered || focused || value);

  const onMouseEnter = React.useCallback(() => setHovered(true), []);
  const onMouseLeave = React.useCallback(() => setHovered(false), []);
  const onFocus = React.useCallback(() => setFocused(true), []);
  const onBlur = React.useCallback(() => setFocused(false), []);
  const onSearch = React.useCallback(e => onChange(e.target.value), [onChange]);

  return (
    <div className={classNames("d2p-ItemSearchBox", {"d2p-expanded": expanded})} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <input type="text" value={value} onChange={onSearch} onFocus={onFocus} onBlur={onBlur} spellCheck={false}/>
      <FontAwesomeIcon icon={faSearch} className="d2p-search-icon"/>
    </div>
  );
});

function selectItems(state) {
  const items = {...state.items};
  const ids = new Set();
  for (const profile of state.profiles) {
    for (const id of profile.inventory) ids.add(id);
    for (const id of profile.cube) ids.add(id);
    for (const id of Object.values(profile.items)) ids.add(id);
    for (const id of Object.values(profile.mercItems)) ids.add(id);
  }
  for (const id in presetItems) {
    if (ids.has(id)) items[id] = presetItems[id];
  }
  return items;
}

export default React.memo(function EquipmentPane() {
  const [path, setPath] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const setEditing = EditingContext.useStateSetter("itemSlotEditing");
  const inventoryIndex = EditingContext.useStateView("inventorySlotEditing");
  const cubeIndex = EditingContext.useStateView("cubeSlotEditing");
  const overlays = React.useContext(OverlayContext);
  const profileClass = Store.useProfile(profile => profile.class);
  const profileMerc = Store.useProfile(profile => profile.merc);

  React.useEffect(() => setSearch(''), [path]);

  const store = Store.useStore();
  const items = Store.useSelector(selectItems);
  const types = React.useMemo(() => {
    const types = new Set();
    for (const item of Object.values(items)) {
      const base = Data.items[item.base];
      if (base) types.add(base.type);
    }
    return [...types];
  }, [items]);

  const onEdit = React.useCallback(params => {
    if (params.type === "item") {
      const item = store.getState().items[params.id];
      if (item) setPath([...navigateToItem(itemTypeTree, item), `item/${params.id}`]);
      return true;
    } else if (params.type === "slot") {
      const index = itemTypeTree.children.indexOf(itemSlotGroups[params.slot]);
      if (index >= 0) {
        const root = itemTypeTree.children[index];
        const state = store.getState();
        const profile = state.profiles[state.activeProfile];
        const itemId = (params.slot === "inventory" ? profile.inventory[params.index] :
          (params.slot === "cube" ? profile.cube[params.index] :
          (root.merc ? profile.mercItems[root.slot] : profile.items[root.slot])));
        const item = itemId && (state.items[itemId] || presetItems[itemId]);
        const path = [index];
        if (item) {
          path.push(...navigateToItem(root, item), `item/${itemId}`);
        } else if (params.slot === 'inventory' || params.slot === 'cube') {
          if (!types.some(type => root.filter(type))) {
            path.push('new');
          }
        } else if (!types.some(type => profileCanEquipType(profile, root.slot, type, root.merc))) {
          path.push('new');
        }
        setPath(path);
        return true;
      }
    }
  }, [store, types]);
  EditingContext.useHandler(onEdit);

  let current = itemTypeTree;
  let currentSlot = null;
  for (const index of path) {
    current = current.children?.[index];
    if (current?.hidden) {
      currentSlot = current;
    }
    if (!current) break;
  }

  React.useEffect(() => {
    setEditing(currentSlot?.slotId);
    return () => setEditing(null);
  }, [setEditing, currentSlot]);

  const filterEx = React.useMemo(() => {
    if (!currentSlot || (currentSlot.slotId === 'inventory' || currentSlot.slotId === 'cube')) return null;
    const profile = {class: profileClass, merc: profileMerc};
    return type => profileCanEquipType(profile, currentSlot.slot, type, currentSlot.merc);
  }, [currentSlot, profileClass, profileMerc]);
  const idFilterEx = React.useMemo(() => {
    if (currentSlot?.merc) {
      return id => profileCanEquip({merc: profileMerc}, currentSlot.slot, {base: id}, true)
    }
  }, [currentSlot, profileMerc]);

  const typesFiltered = React.useMemo(() => filterEx ? types.filter(filterEx) : types, [types, filterEx]);

  const profile = Store.useProfile();
  const equippedLoc = {};
  let equippedId = null;
  if (!currentSlot || currentSlot.slotId === 'inventory' ||  currentSlot.slotId === 'cube') {
    for (const id in profile.mercItems) equippedLoc[profile.mercItems[id]] = 'merc';
    for (const id of profile.inventory) equippedLoc[id] = 'inventory';
    for (const id of profile.cube) equippedLoc[id] = 'cube';
    for (const id in profile.items) equippedLoc[profile.items[id]] = 'equipped';
    if (currentSlot?.slotId === 'inventory') equippedId = profile.inventory[inventoryIndex];
    if (currentSlot?.slotId === 'cube') equippedId = profile.cube[cubeIndex];

    for (const id in items) {
      const item = items[id];
      if (item.socketedItems) {
        for (const sid of item.socketedItems) {
          equippedLoc[sid] = 'socketed';
        }
      }
    }
  } else if (currentSlot?.merc) {
    equippedId = profile.mercItems[currentSlot.slot];
    equippedLoc[equippedId] = 'equipped';
  } else {
    equippedId = profile.items[currentSlot.slot];
    equippedLoc[equippedId] = 'equipped';
  }

  const pathLast = path[path.length - 1];
  const idMatch = typeof pathLast === 'string' && pathLast.match(/^item\/(.*)$/);
  const itemId = (idMatch && idMatch[1]) || null;

  const dispatch = Store.useDispatch();
  React.useEffect(() => {
    if (!itemId || !currentSlot) return;
    const state = store.getState();
    const profile = state.profiles[state.activeProfile];
    const location = currentSlot.slotId;
    if (location === "inventory" || location === "cube") {
      const storage = profile[location].slice();
      const storageIndex = location === "inventory" ? inventoryIndex : cubeIndex;
      delete storage[storageIndex];
      const spot = inventoryFindNearest(storage, storageIndex, itemId, state, Data.info[location]);
      if (spot >= 0) {
        if (storage[spot] !== itemId && (inventoryHasDuplicate(storage, itemId, state) || inventoryHasDuplicate(location === "inventory" ? profile.cube : profile.inventory, itemId, state))) {
          overlays.async(<ErrorPopup title={_L`Failed to equip item`} message={_L`Duplicate unique item`}/>);
          return;
        }
        dispatch.profile.setStorage(location, storage => {
          if (storage[spot] === itemId) return storage;
          storage = storage.slice();
          storage[spot] = itemId;
          return storage;
        });
      } else {
        overlays.async(<ErrorPopup title={_L`Failed to equip item`} message={_L`No space available`}/>);
      }
    } else if (!currentSlot.merc && profile.items[currentSlot.slot] !== itemId) {
      const item = state.items[itemId] || presetItems[itemId];
      if (!profileCanEquip(profile, currentSlot.slot, item)) {
        // this should only happen for class specific items
        overlays.async(<ErrorPopup title={_L`Failed to equip item`} message={_L`Class-specific item`}/>);
      } else {
        dispatch.profile.setItem(currentSlot.slot, itemId);
      }
    } else if (currentSlot.merc && profile.mercItems[currentSlot.slot] !== itemId) {
      const item = state.items[itemId] || presetItems[itemId];
      if (!profileCanEquip(profile, currentSlot.slot, item, true)) {
        // this should only happen for class specific items
        overlays.async(<ErrorPopup title={_L`Failed to equip item`} message={_L`Class-specific item`}/>);
      } else {
        dispatch.profile.setMercItem(currentSlot.slot, itemId);
      }
    }
  }, [store, dispatch, currentSlot, itemId, inventoryIndex, cubeIndex, overlays])

  const onSelect = React.useCallback(id => {
    if (id) {
      setPath([...path, ...navigateToItem(current, items[id] || presetItems[id]), `item/${id}`]);
    }
  }, [items, current, path]);

  return (
    <div className="d2p-EquipmentPane">
      <div className="d2p-PaneHeader">
        {!!current && <SearchBox value={search} onChange={setSearch}/>}
        <NavigationBar path={path} setPath={setPath} types={typesFiltered} disabled={pathLast === "new"} equipped={equippedId}/>
      </div>
      <div className="d2p-PaneContent">
        {!!current && <EquipmentList items={items} type={current} filter={filterEx} onSelect={onSelect} search={search} equipped={equippedLoc} merc={currentSlot?.merc}/>}
        {!!itemId && <ItemEditor id={idMatch[1]}/>}
        {pathLast === "new" && <ItemCreator path={path} filter={filterEx} idFilter={idFilterEx} setPath={setPath}/>}
      </div>
    </div>
  );
});
